import type { InventoryRepoPlus } from '@scaleaq/sitecontrol-lib';

import type { SiteWithApis } from '@/stores';
import { InventorySiteSection } from '@/types.ts';
import { localeBaseCompare, siteMetaFromInventoryRepoPlus, sortByName } from '@/utils';

import { InventorySiteGrouping } from '../../inventorySiteSections.types';

type GroupBucket = {
  groupLabel: string;
  items: InventoryRepoPlus[];
};

type SiteBucket = {
  siteId?: string;
  siteTitle: string;
  siteInventories: InventoryRepoPlus[];
  groupsByKey?: Map<string, GroupBucket>;
};

/** One tab per accessible site (global widgets: chart, sensor, pen, event, …). */
export const buildAccessibleSiteSections = (
  sites: readonly SiteWithApis[] | undefined
): InventorySiteSection[] => {
  if (!sites?.length) return [];

  return sortByName([...sites]).map(site => ({
    siteKey: `siteId:${site.id}`,
    siteTitle: site.name,
    siteId: site.id,
    items: [],
  }));
};

export const buildInventorySiteSections = (
  inventories: readonly InventoryRepoPlus[] | undefined,
  options: {
    notAvailableLabel: string;
    groupBy: InventorySiteGrouping;
    /** Accessible sites used to resolve API-reported site inventory ids. */
    sites?: readonly SiteWithApis[];
    /**
     * Flat `id → display name` map used to label parent groups. A camera's parent is resolved by
     * `parentId` regardless of the parent's type (pen, barge, site root…); cross-site coverage and
     * portable-mode scoping are the map source's concern (see `useInventoryDataCtx().inventoryNameById`).
     * Parents not present in the map fall back to {@link notAvailableLabel}.
     */
    inventoryNameById?: ReadonlyMap<string, string>;
  }
): InventorySiteSection[] => {
  if (!inventories?.length) return [];

  const { notAvailableLabel, groupBy, inventoryNameById, sites = [] } = options;
  const bySite = new Map<string, SiteBucket>();

  for (const inventory of inventories) {
    const fallbackSite = siteMetaFromInventoryRepoPlus(inventory, notAvailableLabel);
    const reportedSiteId = getReportedSiteId(inventory);
    const site = sites.find(candidate => candidate.id === reportedSiteId);
    const siteId = site?.id ?? reportedSiteId;
    const siteKey = siteId ? `siteId:${siteId}` : fallbackSite.siteKey;
    const siteTitle = site?.name ?? fallbackSite.siteTitle;

    let bucket = bySite.get(siteKey);
    if (!bucket) {
      bucket = {
        siteId,
        siteTitle,
        siteInventories: [],
        groupsByKey: groupBy === 'none' ? undefined : new Map<string, GroupBucket>(),
      };
      bySite.set(siteKey, bucket);
    }

    bucket.siteInventories.push(inventory);

    if (groupBy === 'parent' && bucket.groupsByKey) {
      const parentName = inventory.parentId
        ? inventoryNameById?.get(inventory.parentId)
        : undefined;
      const groupLabel = parentName ?? notAvailableLabel;
      const groupKey = inventory.parentId ?? `title:${notAvailableLabel}`;

      let groupBucket = bucket.groupsByKey.get(groupKey);
      if (!groupBucket) {
        groupBucket = { groupLabel, items: [] };
        bucket.groupsByKey.set(groupKey, groupBucket);
      }
      groupBucket.items.push(inventory);
    } else if (groupBy === 'type' && bucket.groupsByKey) {
      const groupKey = inventory.type;
      const groupLabel = inventory.type;

      let groupBucket = bucket.groupsByKey.get(groupKey);
      if (!groupBucket) {
        groupBucket = { groupLabel, items: [] };
        bucket.groupsByKey.set(groupKey, groupBucket);
      }
      groupBucket.items.push(inventory);
    }
  }

  return [...bySite.entries()]
    .map(([siteKey, bucket]) => {
      const items = sortByName(bucket.siteInventories);

      const groups = bucket.groupsByKey
        ? [...bucket.groupsByKey.entries()]
            .map(([groupKey, { groupLabel, items: groupItems }]) => ({
              groupKey,
              groupLabel,
              items: sortByName(groupItems),
            }))
            .sort((a, b) => localeBaseCompare(a.groupLabel, b.groupLabel))
        : undefined;

      return {
        siteKey,
        siteTitle: bucket.siteTitle,
        siteId: bucket.siteId,
        items,
        groups,
      };
    })
    .sort((a, b) => localeBaseCompare(a.siteTitle, b.siteTitle));
};

const getReportedSiteId = (inventory: InventoryRepoPlus): string | undefined => {
  const siteId = inventory.siteInfo?.id;
  return siteId || undefined;
};
